/**
 * Barnyard's host adapter for `@cds/mission-tracker`.
 *
 * The package is deliberately framework-agnostic — it takes its token, its navigation and its
 * colours from the host — so this file is the whole of Barnyard's side of that contract. The CNC
 * frontend will write an equivalent ~40 lines against its own auth and router, and get the same
 * components.
 *
 * The theme maps straight onto Barnyard's `--dt-*` CSS custom properties rather than to hex
 * values. Those variables are what the Tailwind config reads, so the embedded tracker follows the
 * app's light/dark theme automatically and cannot drift from it — copying the hexes would look
 * identical today and be wrong the first time a token changes.
 */

import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { MissionTrackerProvider } from '@cds/mission-tracker';
import { acquireToken } from '../../services/tokenHelper';

/**
 * Barnyard's `API_URL` already ends in `/api` (it is `/api` by default, same-origin), because
 * every service here writes `${API_URL}/reports`. The tracker takes an *origin* and appends
 * `/api/…` itself, so the suffix has to come off — passing `/api` straight through would request
 * `/api/api/notifications` and 404, which reads as a missing route rather than a doubled prefix.
 */
export function trackerBaseUrl(apiUrl: string): string {
  return apiUrl.replace(/\/api\/?$/, '');
}

const API_URL: string =
  (window as unknown as { __RUNTIME_CONFIG__?: { API_URL?: string } }).__RUNTIME_CONFIG__?.API_URL ||
  import.meta.env.VITE_API_URL ||
  '/api';
const TRACKER_BASE_URL = trackerBaseUrl(API_URL);

/** Barnyard's semantic tokens, as the tracker's token names. */
const BARNYARD_THEME = {
  fontFamily: 'inherit',
  surface: 'var(--dt-surface)',
  surfaceAlt: 'var(--dt-surface-alt)',
  surfaceHover: 'var(--dt-surface-hover)',
  border: 'var(--dt-border)',
  textPrimary: 'var(--dt-text-primary)',
  textSecondary: 'var(--dt-text-secondary)',
  textMuted: 'var(--dt-text-muted)',
  accent: 'var(--dt-primary)',
  accentText: 'var(--dt-text-on-primary)',
  success: 'var(--dt-success)',
  warning: 'var(--dt-warning)',
  danger: 'var(--dt-error)',
  radius: '6px',
} as const;

export default function MissionTrackerHost({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  // Per-request, not once: `acquireToken` refreshes silently, so the tracker never presents a
  // token that expired while a long-lived page was open.
  const getToken = useCallback(() => acquireToken(), []);

  const onOpenMission = useCallback(
    (missionId: string, deepLink: string) => {
      // Prefer the server-supplied deep link — it is what the notification was addressed to, and
      // it may point somewhere more specific than the mission root. Fall back to the id so a
      // record written before deep links existed still goes somewhere useful.
      navigate(deepLink || `/missions/${missionId}`);
    },
    [navigate],
  );

  return (
    <MissionTrackerProvider
      baseUrl={TRACKER_BASE_URL}
      getToken={getToken}
      onOpenMission={onOpenMission}
      theme={BARNYARD_THEME}
    >
      {children}
    </MissionTrackerProvider>
  );
}
